"use client";

import { useCallback, useMemo, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  OSCOLA_FIELDS as F,
  formatOscola,
  type OscolaEdition,
  type OscolaKind,
  type OscolaLabels,
  oscolaIsReady,
} from "@/lib/tool-kit/legal-styles/oscola-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const OSCOLA_CITATION_GENERATOR_STRINGS = {
  formHeading: "Source details",
  resultsHeading: "Your OSCOLA citations",
  emptyPromptCase:
    "Enter the case name and its neutral citation, law report or court to see your footnotes.",
  emptyPromptAct: "Enter the Act's short title and year to see your footnotes.",
  emptyPromptSi:
    "Enter the instrument's title and its SI number to see your footnotes.",
  emptyPromptHansard:
    "Enter the date, volume and column of the debate to see your footnote.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  kindLabel: "What you are citing",
  kindCase: "A UK case",
  kindAct: "An Act of Parliament",
  kindSi: "A statutory instrument",
  kindHansard: "Hansard",
  editionLabel: "Edition",
  edition5: "OSCOLA 5th edition (2026)",
  edition4: "OSCOLA 4th edition (2012)",
  oscola5Label: "OSCOLA 5th edition",
  oscola4Label: "OSCOLA 4th edition",
  footnote: "Footnote (first citation)",
  subsequent: "Later footnote",
  ibid: "Next footnote, same source",
  tableOfCases: "Table of cases",
  tableOfLegislation: "Table of legislation",
  noteNumberLabel: "Footnote number of the first citation",
  noteNumberPlaceholder: "1",
  noteNumberHint:
    "Used in the later footnote, which points back with (n 1). Left blank, it reads (n x) for you to fill in.",
  caseNameLabel: "Case name",
  caseNamePlaceholder: "Robinson v Chief Constable of West Yorkshire Police",
  caseNameHint:
    "First claimant and first defendant only, no forenames, Re for In re, ex p for ex parte. It is set in italics.",
  neutralLabel: "Neutral citation (judgments from 2001)",
  neutralPlaceholder: "[2018] UKSC 4",
  neutralHint:
    "Year, court and number, as the judgment prints it: [2009] UKHL 5, [2001] EWCA Civ 1117, [2006] EWHC 407 (QB).",
  reportLabel: "Law report",
  reportPlaceholder: "[2018] AC 736",
  reportHint:
    "The best report: the Law Reports (AC, QB, Ch, Fam) first, then WLR or All ER. The year goes in square brackets when it identifies the volume, round brackets when the volumes are numbered on their own.",
  courtLabel: "Court (only when there is no neutral citation)",
  courtPlaceholder: "HL",
  courtHint:
    "HL, PC, CA, QBD or QB, Ch D or Ch, Fam. For an unreported case with no neutral citation, type the court and date: CA, 8 November 1990.",
  paragraphsLabel: "Paragraph pinpoint",
  paragraphsPlaceholder: "21",
  paragraphsHint:
    "Numbers only, separated by commas; a range takes a hyphen: 34, 39, 43-47. We put them in square brackets.",
  pagesLabel: "Page pinpoint (when there are no paragraph numbers)",
  pagesPlaceholder: "580",
  pagesHint: "Used only if the paragraph box is empty.",
  judgeLabel: "Judge (optional)",
  judgePlaceholder: "Lord Reed",
  judgeHint:
    "Only when you quote and the text does not say who spoke: Lord Reed, Laws LJ, Potter J.",
  shortNameLabel: "Short name for later footnotes (optional)",
  shortNamePlaceholder: "Robinson",
  shortNameHint: "Left blank, we use the first-named party.",
  inTextLabel: "The case name is already in my sentence",
  inTextHint:
    "OSCOLA leaves the name out of the footnote when the text gives it.",
  titleLabelAct: "Short title and year",
  titlePlaceholderAct: "Human Rights Act 1998",
  titleHintAct: "No comma before the year, capitals on the major words.",
  regnalLabel: "Regnal year and chapter (older Acts, optional)",
  regnalPlaceholder: "41 Geo 3 c 90",
  regnalHint:
    "Needed for an old Act without a short title. Use the Regnal Year Converter to find the calendar year.",
  jurisdictionLabel: "Jurisdiction or Act number (optional)",
  jurisdictionPlaceholder: "asp 7",
  jurisdictionHint:
    "UK, NI, Northern Ireland, or the number of a Scottish, Welsh or Senedd Act: asp 7, asc 2, anaw 3.",
  partsLabel: "Section or other provision (optional)",
  partsPlaceholderAct: "s 3(1)",
  partsHintAct:
    "ss 1(1) and 4(3), sch 1 para 3, pt 2. Use the short forms: s, ss, sub-s, para, sch, pt.",
  shortTitleLabel: "Short form for later footnotes (optional)",
  shortTitlePlaceholderAct: "HRA 1998",
  shortTitleHint:
    "Initials of the main words, always with the year. We put it in brackets after the first citation.",
  titleLabelSi: "Title of the instrument, with its year",
  titlePlaceholderSi: "Eggs and Chicks (England) Regulations 2009",
  siNumberLabel: "SI number",
  siNumberPlaceholder: "2009/2163",
  siNumberHint:
    "Year and serial number. We add SI; type SSI 2021/489 for a Scottish instrument, SR 2022/50 for a Northern Ireland rule.",
  welshLabel: "Welsh SI number (optional)",
  welshPlaceholder: "W 245",
  partsPlaceholderSi: "reg 7(2)",
  partsHintSi: "reg, regs, art, arts, r, rr, sch, para.",
  shortTitlePlaceholderSi: "UTCCR 1999",
  houseLabel: "House",
  houseCommons: "House of Commons (HC)",
  houseLords: "House of Lords (HL)",
  writtenLabel: "A written answer or statement",
  writtenHint:
    "Commons written answers take a W after the column number (505W), Lords written answers WA before it (WA261).",
  dateLabel: "Date of the sitting",
  datePlaceholder: "YYYY-MM-DD",
  volumeLabel: "Volume",
  volumePlaceholder: "360",
  columnsLabel: "Column(s)",
  columnsPlaceholder: "1501-1525",
  columnsHint:
    "Hansard is cited by column, not page. A range takes a hyphen: we shorten it to 1501–25.",
  caseNote4:
    "OSCOLA 4th edition: neutral citation, then the best law report, then the court in brackets when there is no neutral citation. Paragraph pinpoints go in square brackets; page pinpoints stand alone. In the next footnote ibid may stand in for the same source; do not switch back and forth between ibid and (n x).",
  caseNote5:
    "OSCOLA 5th edition: it calls the neutral citation a medium neutral citation, asks for a court identifier when there is none, and no longer uses ibid — every later citation is the short name plus (n x). Medium neutral citations before 2001 should never be used.",
  actNote4:
    "OSCOLA 4th edition: short title and year in roman, a comma, then the provision. A short form goes in brackets after the first citation and can then be used without a cross-reference.",
  actNote5:
    "OSCOLA 5th edition: the same layout; a short form now goes in single quotation marks. Leave out “the” before the Act's title in a footnote.",
  siNote4:
    "OSCOLA 4th edition: title, year, a comma, then the SI number, then the provision. Old statutory rules and orders use SR & O.",
  siNote5:
    "OSCOLA 5th edition: the same layout. Rules of court (CPR, FPR) are cited without an SI number.",
  hansardNote4:
    "OSCOLA 4th edition: the House, Deb, the full date, volume and column. Commons written answers add W after the column; Lords written answers put WA before it.",
  hansardNote5:
    "OSCOLA 5th edition: the same form. Its Hansard section does not repeat the W and WA markers for written answers, but Hansard prints them, so keep them unless your faculty says otherwise. Public Bill Committee debates are cited by the Bill's title and Deb instead.",
  exampleCase: {
    name: "Robinson v Chief Constable of West Yorkshire Police",
    neutral: "[2018] UKSC 4",
    report: "[2018] AC 736",
    paragraphs: "21",
    judge: "Lord Reed",
    noteNumber: "1",
  },
  exampleReported: {
    name: "Donoghue v Stevenson",
    report: "[1932] AC 562",
    court: "HL",
    pages: "580",
    judge: "Lord Atkin",
    noteNumber: "12",
  },
  exampleAct: {
    title: "Human Rights Act 1998",
    parts: "s 3(1)",
    shortTitle: "HRA 1998",
  },
  exampleOldAct: {
    title: "Crown Debts Act 1801",
    regnal: "41 Geo 3 c 90",
  },
  exampleSi: {
    title: "Eggs and Chicks (England) Regulations 2009",
    siNumber: "2009/2163",
    parts: "reg 7(2)",
    shortTitle: "ECR 2009",
  },
  exampleHansard: {
    house: "HC",
    date: "1940-05-13",
    volume: "360",
    columns: "1501-1525",
  },
} as const;

type Strings = typeof OSCOLA_CITATION_GENERATOR_STRINGS;

function caseFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.name,
      kind: "text",
      label: strings.caseNameLabel,
      placeholder: strings.caseNamePlaceholder,
      hint: strings.caseNameHint,
      wide: true,
    },
    {
      id: F.neutral,
      kind: "text",
      label: strings.neutralLabel,
      placeholder: strings.neutralPlaceholder,
      hint: strings.neutralHint,
    },
    {
      id: F.report,
      kind: "text",
      label: strings.reportLabel,
      placeholder: strings.reportPlaceholder,
      hint: strings.reportHint,
    },
    {
      id: F.court,
      kind: "text",
      label: strings.courtLabel,
      placeholder: strings.courtPlaceholder,
      hint: strings.courtHint,
    },
    {
      id: F.paragraphs,
      kind: "text",
      label: strings.paragraphsLabel,
      placeholder: strings.paragraphsPlaceholder,
      hint: strings.paragraphsHint,
    },
    {
      id: F.pages,
      kind: "text",
      label: strings.pagesLabel,
      placeholder: strings.pagesPlaceholder,
      hint: strings.pagesHint,
    },
    {
      id: F.judge,
      kind: "text",
      label: strings.judgeLabel,
      placeholder: strings.judgePlaceholder,
      hint: strings.judgeHint,
    },
    {
      id: F.shortName,
      kind: "text",
      label: strings.shortNameLabel,
      placeholder: strings.shortNamePlaceholder,
      hint: strings.shortNameHint,
    },
    {
      id: F.noteNumber,
      kind: "text",
      label: strings.noteNumberLabel,
      placeholder: strings.noteNumberPlaceholder,
      hint: strings.noteNumberHint,
    },
    {
      id: F.inText,
      kind: "checkbox",
      label: strings.inTextLabel,
      hint: strings.inTextHint,
    },
  ];
}

function actFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabelAct,
      placeholder: strings.titlePlaceholderAct,
      hint: strings.titleHintAct,
    },
    {
      id: F.parts,
      kind: "text",
      label: strings.partsLabel,
      placeholder: strings.partsPlaceholderAct,
      hint: strings.partsHintAct,
    },
    {
      id: F.regnal,
      kind: "text",
      label: strings.regnalLabel,
      placeholder: strings.regnalPlaceholder,
      hint: strings.regnalHint,
    },
    {
      id: F.jurisdiction,
      kind: "text",
      label: strings.jurisdictionLabel,
      placeholder: strings.jurisdictionPlaceholder,
      hint: strings.jurisdictionHint,
    },
    {
      id: F.shortTitle,
      kind: "text",
      label: strings.shortTitleLabel,
      placeholder: strings.shortTitlePlaceholderAct,
      hint: strings.shortTitleHint,
    },
  ];
}

function siFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabelSi,
      placeholder: strings.titlePlaceholderSi,
      wide: true,
    },
    {
      id: F.siNumber,
      kind: "text",
      label: strings.siNumberLabel,
      placeholder: strings.siNumberPlaceholder,
      hint: strings.siNumberHint,
    },
    {
      id: F.welsh,
      kind: "text",
      label: strings.welshLabel,
      placeholder: strings.welshPlaceholder,
    },
    {
      id: F.parts,
      kind: "text",
      label: strings.partsLabel,
      placeholder: strings.partsPlaceholderSi,
      hint: strings.partsHintSi,
    },
    {
      id: F.shortTitle,
      kind: "text",
      label: strings.shortTitleLabel,
      placeholder: strings.shortTitlePlaceholderSi,
      hint: strings.shortTitleHint,
    },
  ];
}

function hansardFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.house,
      kind: "select",
      label: strings.houseLabel,
      options: [
        { value: "HC", label: strings.houseCommons },
        { value: "HL", label: strings.houseLords },
      ],
    },
    {
      id: F.date,
      kind: "date",
      label: strings.dateLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.volume,
      kind: "text",
      label: strings.volumeLabel,
      placeholder: strings.volumePlaceholder,
    },
    {
      id: F.columns,
      kind: "text",
      label: strings.columnsLabel,
      placeholder: strings.columnsPlaceholder,
      hint: strings.columnsHint,
    },
    {
      id: F.written,
      kind: "checkbox",
      label: strings.writtenLabel,
      hint: strings.writtenHint,
    },
  ];
}

function fieldsFor(strings: Strings, kind: OscolaKind): SourceField[] {
  switch (kind) {
    case "case":
      return caseFields(strings);
    case "act":
      return actFields(strings);
    case "si":
      return siFields(strings);
    default:
      return hansardFields(strings);
  }
}

function exampleFor(strings: Strings, kind: OscolaKind): SourceValues {
  switch (kind) {
    case "case":
      return strings.exampleCase;
    case "act":
      return strings.exampleAct;
    case "si":
      return strings.exampleSi;
    default:
      return strings.exampleHansard;
  }
}

function emptyPromptFor(strings: Strings, kind: OscolaKind): string {
  switch (kind) {
    case "case":
      return strings.emptyPromptCase;
    case "act":
      return strings.emptyPromptAct;
    case "si":
      return strings.emptyPromptSi;
    default:
      return strings.emptyPromptHansard;
  }
}

/** The widget's strings, as the formatter's labels. */
export function oscolaLabels(strings: Strings): OscolaLabels {
  return {
    oscola4: strings.oscola4Label,
    oscola5: strings.oscola5Label,
    footnote: strings.footnote,
    subsequent: strings.subsequent,
    ibid: strings.ibid,
    tableOfCases: strings.tableOfCases,
    tableOfLegislation: strings.tableOfLegislation,
    caseNote4: strings.caseNote4,
    caseNote5: strings.caseNote5,
    actNote4: strings.actNote4,
    actNote5: strings.actNote5,
    siNote4: strings.siNote4,
    siNote5: strings.siNote5,
    hansardNote4: strings.hansardNote4,
    hansardNote5: strings.hansardNote5,
  };
}

export default function OscolaCitationGeneratorWidget() {
  const strings = useToolUi(OSCOLA_CITATION_GENERATOR_STRINGS);
  const [kind, setKind] = useState<OscolaKind>("case");
  const [edition, setEdition] = useState<OscolaEdition>("5");
  const fields = useMemo(() => fieldsFor(strings, kind), [strings, kind]);
  const labels = useMemo(() => oscolaLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatOscola(kind, edition, values, labels),
    [kind, edition, labels]
  );
  const isReady = useCallback(
    (values: SourceValues) => oscolaIsReady(kind, values),
    [kind]
  );
  const builderStrings = useMemo(
    () => ({ ...strings, emptyPrompt: emptyPromptFor(strings, kind) }),
    [strings, kind]
  );

  return (
    <SourceCitationBuilder
      example={exampleFor(strings, kind)}
      fields={fields}
      format={format}
      intro={
        <div className="space-y-4">
          <PillGroup
            label={strings.kindLabel}
            name="oscola-kind"
            onChange={setKind}
            options={[
              { value: "case", label: strings.kindCase },
              { value: "act", label: strings.kindAct },
              { value: "si", label: strings.kindSi },
              { value: "hansard", label: strings.kindHansard },
            ]}
            value={kind}
          />
          <PillGroup
            label={strings.editionLabel}
            name="oscola-edition"
            onChange={setEdition}
            options={[
              { value: "5", label: strings.edition5 },
              { value: "4", label: strings.edition4 },
            ]}
            value={edition}
          />
        </div>
      }
      isReady={isReady}
      strings={builderStrings}
    />
  );
}
